import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactQuill from "react-quill-new";

import "react-quill-new/dist/quill.snow.css";
import {
  Calendar,
  ArrowLeft,
  AlertCircle,
  Loader2,
  FileText,
  Edit3,
  PlusCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Sparkles,
  Globe,
  Hash,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import TextInputComp from "../../../components/customer/TextInputComp";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";

// Import hooks directly from your RTK Query actions destination slice
import {
  useCreatePageMutation,
  useUpdatePageMutation,
  useLazyGetAllPageQuery,
  useLazyGetPageByIdQuery,
} from "../../../redux/actions/Admin/footerManagementAdmin";

// Import Types and Interfaces from project paths
import { GetAllPageData } from "../../../types/Response/CustomerResponse";
import {
  ICreatePageRequest,
  IUpdatePageRequest,
} from "../../../types/Request/CustomerRequest";
import { useAlert } from "../../../components/customer/AlertContext";

export enum WebsitePageType {
  AboutUs = 1,
  PrivacyPolicy = 2,
  TermsAndConditions = 3,
  ReturnAndExchangePolicy = 4,
  CancellationPolicy = 5,
  ShippingPolicy = 6,
}

type ViewMode = "LIST" | "MANAGE";

export const TermsAdmin: React.FC = () => {
  const quillRef = useRef<ReactQuill | null>(null);
  const { showConfirm } = useAlert();

  // ==========================================
  // RTK QUERY INITIALIZATION
  // ==========================================
  const [triggerGetAllPage, { data: faqApiResponse, isLoading, isError }] =
    useLazyGetAllPageQuery();
  const [
    triggerGetPageById,
    { data: pageByIdResponse, isLoading: isLoadingPageById },
  ] = useLazyGetPageByIdQuery();
  const [createPage, { isLoading: isCreating }] = useCreatePageMutation();
  const [updatePage, { isLoading: isUpdating }] = useUpdatePageMutation();
  const [uploadFile, { isLoading: isUploadingMedia }] = useUploadFileMutation();

  // Options configuration matching C# enum & pageType strings
  const pageTypeOptions: Option[] = [
    { id: "1", label: "About Us", value: "AboutUs" },
    { id: "2", label: "Privacy Policy", value: "PrivacyPolicy" },
    { id: "3", label: "Terms & Conditions", value: "TermsAndConditions" },
    { id: "4", label: "Return & Exchange Policy", value: "ReturnAndExchangePolicy" },
    { id: "5", label: "Cancellation Policy", value: "CancellationPolicy" },
    { id: "6", label: "Shipping Policy", value: "ShippingPolicy" },
  ];

  const [selectedPageType, setSelectedPageType] = useState<Option | null>(
    pageTypeOptions[0]
  );
  const [currentView, setCurrentView] = useState<ViewMode>("LIST");

  // Form Inputs State
  const [titleInput, setTitleInput] = useState("");
  const [contentHtmlInput, setContentInput] = useState("");
  const [isActiveInput, setIsActiveInput] = useState(true);

  // Trigger lazy endpoint call on mount
  useEffect(() => {
    triggerGetAllPage();
  }, [triggerGetAllPage]);

  // Extract records matching array contract
  const websitePages: GetAllPageData[] = faqApiResponse?.value?.data || [];

  // 1. Find matching summary row from list
  const matchedSummaryData = useMemo(() => {
    if (!selectedPageType) return null;
    return (
      websitePages.find(
        (p) =>
          String(p.pageType).toLowerCase() ===
          selectedPageType.value.toLowerCase()
      ) || null
    );
  }, [selectedPageType, websitePages]);

  // 2. Automatically trigger triggerGetPageById when matched data exists with ID
  useEffect(() => {
    if (matchedSummaryData?.id) {
      triggerGetPageById(matchedSummaryData.id);
    }
  }, [matchedSummaryData, triggerGetPageById]);

  // 3. Priority Page Data
  const activePageData: GetAllPageData | null =
    pageByIdResponse?.value?.data || matchedSummaryData;

  // Real-time Text Stats for Editor
  const editorStats = useMemo(() => {
    const text = contentHtmlInput.replace(/<[^>]*>/g, "").trim();
    const wordCount = text ? text.split(/\s+/).length : 0;
    const charCount = text.length;
    return { wordCount, charCount };
  }, [contentHtmlInput]);

  // ==========================================
  // EDITOR IMAGE UPLOAD PIPELINE
  // ==========================================
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const response = await uploadFile({
          File: file,
          FolderName: "CMS/EditorImages",
        }).unwrap();

        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          quill.insertEmbed(range?.index || 0, "image", response.fileUrl);
        }
      } catch (error) {
        console.error("Rich text image injection failed:", error);
        showConfirm({
          title: "Image Upload Failed",
          message: "The image couldn't be uploaded. Please try again.",
          type: "error",
          showCancel: false,
          confirmText: "Acknowledge",
        });
      }
    };
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [uploadFile]
  );

  // ==========================================
  // ACTION WORKSPACE HANDLERS
  // ==========================================
  const handleOpenManageWorkspace = () => {
    if (activePageData) {
      setTitleInput(activePageData.title);
      setContentInput(activePageData.content);
      setIsActiveInput(activePageData.isActive);
    } else {
      setTitleInput(selectedPageType ? `${selectedPageType.label}` : "");
      setContentInput("");
      setIsActiveInput(true);
    }
    setCurrentView("MANAGE");
  };

  const handleFormSaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPageType) return;

    const enumIdMap: Record<string, number> = {
      AboutUs: 1,
      PrivacyPolicy: 2,
      TermsAndConditions: 3,
      ReturnAndExchangePolicy: 4,
      CancellationPolicy: 5,
      ShippingPolicy: 6,
    };
    const targetPageTypeNumber = enumIdMap[selectedPageType.value] || 1;

    try {
      if (activePageData) {
        const updatePayload: IUpdatePageRequest = {
          id: activePageData.id,
          pageType: targetPageTypeNumber,
          title: titleInput.trim(),
          content: contentHtmlInput,
          isActive: isActiveInput,
        };
        await updatePage(updatePayload).unwrap();

        showConfirm({
          title: "Page Updated",
          message: `${selectedPageType.label} has been updated successfully.`,
          type: "success",
          showCancel: false,
          confirmText: "Return to Dashboard",
        });
      } else {
        const createPayload: ICreatePageRequest = {
          pageType: targetPageTypeNumber,
          title: titleInput.trim(),
          content: contentHtmlInput,
          isActive: isActiveInput,
        };
        await createPage(createPayload).unwrap();

        showConfirm({
          title: "Page Created",
          message: `${selectedPageType.label} has been created successfully.`,
          type: "success",
          showCancel: false,
          confirmText: "Return to Dashboard",
        });
      }

      triggerGetAllPage();
      setCurrentView("LIST");
    } catch (error) {
      console.error("Payload transaction submission failed:", error);
      showConfirm({
        title: "Something Went Wrong",
        message: "We couldn't save your changes. Please try again.",
        type: "error",
        showCancel: false,
        confirmText: "Dismiss",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center gap-3 text-slate-500 font-medium">
        <Loader2 className="w-8 h-8 animate-spin text-[#2F5E2E]" />
        <span className="text-xs font-semibold tracking-wide text-slate-400">
          Loading Page Workspace...
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 my-8 max-w-xl mx-auto rounded-2xl bg-rose-50 border border-rose-200 text-center text-rose-600 font-medium space-y-2">
        <AlertCircle size={28} className="mx-auto text-rose-500" />
        <p className="text-sm font-bold">Unable to load pages</p>
        <p className="text-xs text-rose-400">
          Please check your network connection or try refreshing the page.
        </p>
      </div>
    );
  }

  const isSubmitting = isCreating || isUpdating;

  return (
    <div className="p-4 sm:p-6 min-h-screen text-slate-800 text-left max-w-7xl mx-auto w-full box-border relative">
      {/* OVERLAY LOADING LOADER */}
      {(isUploadingMedia || isLoadingPageById || isSubmitting) && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center transition-all">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-4 shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-150">
            <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
            <div className="flex flex-col">
              <span className="text-xs font-bold text-slate-800">
                {isSubmitting
                  ? "Saving Changes..."
                  : isLoadingPageById
                  ? "Fetching Page Details..."
                  : "Uploading Image..."}
              </span>
              <span className="text-[10px] text-slate-400 font-medium">
                Please wait a moment
              </span>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 1: DATA DISPLAY DASHBOARD HUB */}
      {currentView === "LIST" && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* HEADER BAR */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
            <div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-[#2F5E2E]/10 rounded-lg text-[#2F5E2E]">
                  <Globe size={20} />
                </div>
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                  CMS Page Manager
                </h1>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-1">
                Manage live policy pages, legal terms, and brand information across your platform.
              </p>
            </div>

            <CommonButton
              onClick={handleOpenManageWorkspace}
              text={activePageData ? "Edit Page Content" : "Create New Page"}
              icon={
                activePageData ? (
                  <Edit3 size={15} />
                ) : (
                  <PlusCircle size={15} />
                )
              }
              variant="filled"
              className="bg-[#2F5E2E] text-white font-bold text-xs px-5 h-10 shadow-sm hover:bg-[#1f3f1e] transition-all rounded-xl cursor-pointer self-start md:self-auto"
            />
          </div>

          {/* PAGE SELECTOR TABS & DROPDOWN */}
          <div className="bg-white border border-slate-200/80 rounded-2xl p-2 shadow-2xs space-y-3">
            {/* Desktop Tabs */}
            <div className="hidden lg:flex items-center gap-1 overflow-x-auto p-1 scrollbar-none">
              {pageTypeOptions.map((opt) => {
                const isSelected = selectedPageType?.value === opt.value;
                const hasData = websitePages.some(
                  (p) =>
                    String(p.pageType).toLowerCase() ===
                    opt.value.toLowerCase()
                );

                return (
                  <button
                    key={opt.id}
                    onClick={() => setSelectedPageType(opt)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      isSelected
                        ? "bg-[#2F5E2E] text-white shadow-xs"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <span>{opt.label}</span>
                    <span
                      className={`w-2 h-2 rounded-full ${
                        hasData
                          ? isSelected
                            ? "bg-emerald-300"
                            : "bg-emerald-500"
                          : isSelected
                          ? "bg-amber-300"
                          : "bg-amber-400"
                      }`}
                      title={hasData ? "Page Exists" : "Page Not Created"}
                    />
                  </button>
                );
              })}
            </div>

            {/* Mobile Dropdown Fallback */}
            <div className="block lg:hidden">
              <Dropdown
                options={pageTypeOptions}
                value={selectedPageType}
                onChange={(option) => setSelectedPageType(option)}
                className="w-full"
                innerClassName="bg-slate-50 border-slate-200 font-bold text-xs !py-2.5"
                iconPosition="none"
                showLabel
                label="Select Target Page"
              />
            </div>
          </div>

          {/* MAIN PAGE PREVIEW CARD */}
          {activePageData ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-2xs">
              {/* Card Meta Header */}
              <div className="px-6 py-4 bg-slate-50/80 border-b border-slate-200/60 flex flex-wrap justify-between items-center gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#2F5E2E]" />
                    <h2 className="text-base font-black text-slate-900 tracking-tight">
                      {activePageData.title}
                    </h2>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={13} className="text-slate-400" />
                      Updated:{" "}
                      {activePageData.createdOn
                        ? activePageData.createdOn.split("T")[0]
                        : "N/A"}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1 font-mono text-slate-400">
                      <Hash size={12} />
                      {activePageData.id}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border ${
                      activePageData.isActive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}
                  >
                    {activePageData.isActive ? (
                      <>
                        <Eye size={12} /> Published
                      </>
                    ) : (
                      <>
                        <EyeOff size={12} /> Hidden
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* HTML Content Render */}
              <div className="p-4 sm:p-5 lg:p-6">
                <div
                  className="
                    min-h-[180px]
                    text-[13px]
                    sm:text-[14px]
                    text-slate-600
                    leading-[1.45]
                    break-words
                    whitespace-normal

                    [&_p]:m-0
                    [&_p]:mb-3
                    [&_p]:leading-[1.45]

                    [&_h1]:text-[24px]
                    [&_h1]:font-bold
                    [&_h1]:leading-[1.2]
                    [&_h1]:text-slate-900
                    [&_h1]:mt-5
                    [&_h1]:mb-3

                    [&_h2]:text-[20px]
                    [&_h2]:font-bold
                    [&_h2]:leading-[1.25]
                    [&_h2]:text-slate-900
                    [&_h2]:mt-5
                    [&_h2]:mb-2

                    [&_h3]:text-[17px]
                    [&_h3]:font-bold
                    [&_h3]:leading-[1.3]
                    [&_h3]:text-slate-900
                    [&_h3]:mt-4
                    [&_h3]:mb-2

                    [&_strong]:font-bold
                    [&_b]:font-bold
                    [&_strong]:text-slate-900
                    [&_em]:italic
                    [&_i]:italic
                    [&_u]:underline
                    [&_u]:underline-offset-2
                    [&_s]:line-through
                    [&_strike]:line-through

                    [&_ol]:list-decimal
                    [&_ol]:pl-5
                    [&_ol]:mb-3
                    [&_ol]:space-y-0.5
                    [&_ul]:list-disc
                    [&_ul]:pl-5
                    [&_ul]:mb-3
                    [&_ul]:space-y-0.5
                    [&_li]:leading-[1.45]
                    [&_li]:pl-1
                    [&_li]:mb-0.5
                    [&_li[data-list='bullet']]:list-disc
                    [&_li[data-list='ordered']]:list-decimal

                    [&_a]:text-[#2F5E2E]
                    [&_a]:font-medium
                    [&_a]:underline
                    [&_a]:underline-offset-2
                    hover:[&_a]:text-[#244A24]

                    [&_blockquote]:border-l-2
                    [&_blockquote]:border-slate-300
                    [&_blockquote]:pl-3
                    [&_blockquote]:my-3
                    [&_blockquote]:italic
                    [&_blockquote]:text-slate-500

                    [&_img]:max-w-full
                    [&_img]:h-auto
                    [&_img]:rounded-lg
                    [&_img]:shadow-sm
                    [&_img]:my-4

                    [&_hr]:border-0
                    [&_hr]:border-t
                    [&_hr]:border-slate-200
                    [&_hr]:my-4

                    [&_.ql-indent-1]:pl-8
                    [&_.ql-indent-2]:pl-16
                    [&_.ql-indent-3]:pl-24
                    [&_.ql-indent-4]:pl-32

                    [&_.ql-align-center]:text-center
                    [&_.ql-align-right]:text-right
                    [&_.ql-align-justify]:text-justify

                    [&_table]:w-full
                    [&_table]:border-collapse
                    [&_table]:my-4
                    [&_th]:border
                    [&_th]:border-slate-300
                    [&_th]:px-3
                    [&_th]:py-2
                    [&_th]:font-bold
                    [&_th]:text-left
                    [&_th]:text-slate-900
                    [&_td]:border
                    [&_td]:border-slate-300
                    [&_td]:px-3
                    [&_td]:py-2

                    max-sm:[&_h1]:text-[21px]
                    max-sm:[&_h2]:text-[18px]
                    max-sm:[&_h3]:text-[16px]
                  "
                  dangerouslySetInnerHTML={{
                    __html: activePageData.content,
                  }}
                />
              </div>
            </div>
          ) : (
            /* EMPTY STATE CARD */
            <div className="border-2 border-dashed border-slate-200 bg-white rounded-2xl p-12 text-center text-slate-400 flex flex-col items-center justify-center space-y-3">
              <div className="p-3 bg-slate-50 rounded-full text-slate-300">
                <AlertCircle size={32} />
              </div>
              <div className="max-w-xs space-y-1">
                <p className="text-sm font-bold text-slate-700">
                  No page found for "{selectedPageType?.label}"
                </p>
                <p className="text-xs text-slate-400">
                  This page hasn't been set up yet. Click below to create content for this section.
                </p>
              </div>
              <CommonButton
                onClick={handleOpenManageWorkspace}
                text={`Create ${selectedPageType?.label}`}
                icon={<PlusCircle size={14} />}
                variant="filled"
                className="mt-2 bg-[#2F5E2E] text-white font-bold text-xs px-4 h-9 shadow-xs hover:bg-[#1f3f1e] rounded-xl cursor-pointer"
              />
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: CANVAS CORE WORKSPACE EDITING FORM */}
      {currentView === "MANAGE" && (
        <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-bottom duration-200">
          {/* Top Return Header */}
          <div className="flex items-center justify-between gap-4 pb-2 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentView("LIST")}
                className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition cursor-pointer shadow-3xs"
                type="button"
                title="Back to pages"
              >
                <ArrowLeft size={16} />
              </button>
              <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">
                  {activePageData
                    ? `Edit ${selectedPageType?.label}`
                    : `Create ${selectedPageType?.label}`}
                </h2>
                <p className="text-xs text-slate-400 font-medium mt-0.5">
                  Update title, rich formatting, and publishing status.
                </p>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-2 text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg">
              <Sparkles size={14} className="text-[#2F5E2E]" />
              <span>CMS Editor Workspace</span>
            </div>
          </div>

          {/* Form Engine */}
          <form
            onSubmit={handleFormSaveSubmit}
            className="bg-white border border-slate-200/80 rounded-2xl p-6 space-y-6 shadow-2xs"
          >
            <TextInputComp
              showLabel
              label="Page Title"
              placeholder="Enter page title"
              value={titleInput}
              onChangeText={setTitleInput}
              showStar
            />

            {/* Rich Text Field Area */}
            <div className="space-y-2 text-left">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                  <span>Page Content</span>
                  <span className="text-rose-500">*</span>
                </label>
                <div className="text-[11px] text-slate-400 font-medium space-x-2">
                  <span>{editorStats.wordCount} words</span>
                  <span>•</span>
                  <span>{editorStats.charCount} chars</span>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col focus-within:border-[#2F5E2E] focus-within:ring-1 focus-within:ring-[#2F5E2E] transition-all">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={contentHtmlInput}
                  onChange={setContentInput}
                  modules={quillModules}
                  placeholder="Write your page content here..."
                  className="flex-1 text-xs sm:text-sm"
                  style={{ height: "320px" }}
                />
              </div>
            </div>

            {/* Status Switch Option */}
            <div className="flex items-start select-none bg-slate-50 border border-slate-200 p-4 rounded-xl cursor-pointer hover:bg-slate-100/60 transition-colors">
              <input
                id="isPageActiveInputToggle"
                type="checkbox"
                checked={isActiveInput}
                onChange={(e) => setIsActiveInput(e.target.checked)}
                className="w-4 h-4 text-[#2F5E2E] border-slate-300 rounded focus:ring-[#2F5E2E] cursor-pointer mt-0.5"
              />
              <label
                htmlFor="isPageActiveInputToggle"
                className="ml-3 text-xs font-bold text-slate-800 cursor-pointer flex flex-col leading-tight"
              >
                <span>Publish Page immediately</span>
                <span className="text-[11px] text-slate-400 font-normal mt-0.5">
                  When enabled, this content is immediately visible to website visitors.
                </span>
              </label>
            </div>

            {/* Action Bar */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <CommonButton
                text="Cancel"
                variant="outline"
                className="px-5 h-10 text-xs border-slate-200 font-semibold hover:bg-slate-50"
                onClick={() => setCurrentView("LIST")}
              />
              <CommonButton
                text={
                  activePageData ? "Save Changes" : "Publish Page"
                }
                icon={<CheckCircle2 size={15} />}
                variant="filled"
                disabled={
                  !titleInput.trim() ||
                  !contentHtmlInput.trim() ||
                  contentHtmlInput === "<p><br></p>" ||
                  isSubmitting
                }
                className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-xs font-bold px-6 h-10 shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TermsAdmin;